import { AxiosError, InternalAxiosRequestConfig } from 'axios'
import AuthorizationContext, {
    DestinationAuthorityMap,
    isTokenNotExpired,
    OfficeTokenPayload,
    SecurityAuthority,
} from '../../context/AuthorizationContext'
import { Destination } from '../../context/AxiosContext'
import { useContext, useRef } from 'react'
import { jwtDecode } from 'jwt-decode'

export default function useTokenInjector() {
    const props = useContext(AuthorizationContext)
    // Sync refs during render rather than via useEffect. The interceptor is registered once via
    // Container's useMount and reads these refs via closure. If we synced via useEffect, a child
    // component's effect could fire an axios request (e.g. FileProvider's /file/list) before our
    // effect runs to update the ref, causing the interceptor to reject with TOKEN_MISSING.
    const tokensRef = useRef(props.tokens)
    tokensRef.current = props.tokens
    const getBaseUrlRef = useRef(props.getBaseUrl)
    getBaseUrlRef.current = props.getBaseUrl
    const updateTokensRef = useRef(props.updateTokens)
    updateTokensRef.current = props.updateTokens
    const lastLoggedUtiRef = useRef<Record<string, string | undefined>>({})

    return (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> | InternalAxiosRequestConfig => {
        // Only intercept when the base URL should default to the server. This way we don't send
        // headers to ineligible hosts.
        if ((!config.baseURL && !(config.url || '').match(/^http(s)?:\/\//)) || Object.values(Destination).includes(config.baseURL as Destination)) {
            const destination = (config.baseURL as Destination) || Destination.Hammock
            config.baseURL = getBaseUrlRef.current?.(destination)
            config.headers['Destination'] = destination
        } else if (!config.headers['Destination'] || config.headers['Authorization']) {
            return config
        }

        const destination = config.headers['Destination'] as Destination
        const authority = DestinationAuthorityMap.get(destination)
        const token = tokensRef.current?.[authority]

        // Pre-flight refresh: if the cached token is inside the expiry buffer (or missing), wait
        // for an update before firing the request. The response interceptor still retries on a
        // 401 as a safety net, but doing the refresh up front prevents the user-visible
        // "uitgelogd" error path when the scheduled refresh has not yet completed.
        // `updateTokens` deduplicates via `tokensRequestRef`, so parallel callers share one
        // refresh. Skipped for WebPubSub (not used by axios) — DestinationAuthorityMap never
        // maps to that authority.
        if (!isTokenNotExpired(token) && updateTokensRef.current && authority !== SecurityAuthority.WebPubSub) {
            return updateTokensRef.current(authority).then(() => injectAuthorization(config, destination, authority))
        }

        return injectAuthorization(config, destination, authority)
    }

    function injectAuthorization(
        config: InternalAxiosRequestConfig,
        destination: Destination,
        authority: SecurityAuthority,
    ): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> {
        const fresh = tokensRef.current?.[authority]

        if (fresh?.access_token) {
            const decodedToken = jwtDecode<OfficeTokenPayload>(fresh?.access_token)
            if (lastLoggedUtiRef.current[authority] !== decodedToken.uti) {
                lastLoggedUtiRef.current[authority] = decodedToken.uti
                console.debug('AuthorizationProvider: useTokenInjector: using', destination, 'token:', decodedToken.uti)
            }
        }

        if (!fresh?.access_token) {
            return Promise.reject(new AxiosError(`Er is geen toegangstoken aanwezig voor '${authority}'.`, 'TOKEN_MISSING', config))
        } else if (fresh?.token_type !== 'Bearer') {
            return Promise.reject(
                new AxiosError(`De toegangstoken voor '${authority}' is geen bearer token (${fresh?.token_type}).`, 'INVALID_TOKEN_TYPE', config),
            )
        }

        config.headers['Authorization'] = `${fresh.token_type} ${fresh.access_token}`
        config.headers['Destination'] = destination
        return config
    }
}
